/**
 * @fileoverview Questions and answers for the pricing page FAQ.
 *
 * The plan comparison answer is built from the same GET /plans catalog as the
 * comparison table (see planComparisonRows.js), not written down. A written-down
 * "up to 50 stops" is exactly the kind of sentence that drifts from what the
 * backend enforces, and a test that only checks the sentence renders can't
 * notice. So the stop and vehicle numbers, and the list of what Professional
 * adds, are read from the catalog. While the catalog is loading or unavailable
 * the answer states no numbers at all and points at the table.
 *
 * Everything else here is editorial and was checked by hand on 2026-09-15:
 *   - Signed-out visitors can open the examples and solve their own stops, but
 *     save nothing. That is the anonymous tier, not Starter.
 *   - Starter is free, has no trial, and needs no card.
 *   - The Professional trial is 30 days and DOES need a card (Clerk's "require
 *     payment method for free trials" is on). Cancelling before it ends avoids
 *     the charge.
 * If any of those change, change the copy and the pinned tests together.
 */

import { findPlanInCatalog } from 'functions/entitlements/featureEntitlements';

/*
 * What Professional adds over Starter, in the order an operator is likely to
 * care. A phrase is used only when the catalog says Professional has the
 * feature AND Starter does not, so a feature moved down to Starter (or taken
 * away from Professional) drops out of the answer on its own.
 *
 * Road distances are North America only: the road-network graph covers North
 * America, and everywhere else solves on straight-line distances.
 */
const PROFESSIONAL_ADDS = [
  ['road_network_costs', 'road distances and drive times in North America'],
  ['multi_day', 'multi-day routes'],
  ['resupply', 'mid-route resupply'],
  ['heterogeneous_travel_costs', 'separate travel times for each vehicle type'],
  ['fixed_assignments', 'locking a stop to a particular vehicle'],
];

function maxRows(plan, key) {
  const raw = plan && plan.limits && plan.limits[key] ? plan.limits[key].maxRows : undefined;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

function hasFeature(plan, key) {
  const features = plan && plan.features;
  if (!features || typeof features !== 'object') return null;
  if (!Object.prototype.hasOwnProperty.call(features, key)) return null;
  return features[key] === true;
}

/** "a", "a and b", "a, b and c" */
function joinList(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function sizeClause(plan) {
  const stops = maxRows(plan, 'locations');
  const vehicles = maxRows(plan, 'vehicles');
  if (stops === null || vehicles === null) return null;
  return `up to ${stops} stops and ${vehicles} vehicles per run`;
}

/**
 * The Starter-versus-Professional answer, derived from the plan catalog.
 *
 * @param {Array<object>|null|undefined} catalog - Plans from GET /plans.
 * @returns {string}
 */
export function planDifferenceAnswer(catalog) {
  const both =
    'Both plans include the Vehicle Routing and Pickup & Dropoff optimizers, time windows and vehicle capacities.';
  const free = findPlanInCatalog(catalog, 'free');
  const professional = findPlanInCatalog(catalog, 'professional');
  const starterSize = sizeClause(free);
  const professionalSize = sizeClause(professional);

  if (!starterSize || !professionalSize) {
    return `${both} Professional takes bigger problems and adds features Starter doesn't have. The comparison table on this page lists each plan's limits.`;
  }

  const straightLine = hasFeature(free, 'road_network_costs') === false ? ', using straight-line distances' : '';
  const adds = PROFESSIONAL_ADDS
    .filter(([key]) => hasFeature(professional, key) === true && hasFeature(free, key) === false)
    .map(([, phrase]) => phrase);
  const addsClause = adds.length ? `, and adds ${joinList(adds)}` : '';

  return `${both} Starter handles ${starterSize}${straightLine}. Professional handles ${professionalSize}${addsClause}.`;
}

/**
 * The pricing page FAQ.
 *
 * @param {Array<object>|null|undefined} catalog - Plans from GET /plans.
 * @returns {Array<{q: string, a: string}>}
 */
export function buildPricingFaqs(catalog) {
  return [
    {
      q: 'Can I try Optimiciti before I pay anything?',
      a: 'Yes. Open the worked examples, or upload your own stops and solve them, without an account. The free Starter plan adds saved scenarios and needs no credit card.',
    },
    {
      q: 'What is the difference between Starter and Professional?',
      a: planDifferenceAnswer(catalog),
    },
    {
      q: 'How does the 30-day trial work?',
      a: "Professional includes a 30-day free trial. You add a card to start it, and if you cancel from your account settings before the trial ends, you aren't charged.",
    },
    {
      q: 'Can I bring my stops in from a spreadsheet?',
      a: 'Yes. Upload an Excel or CSV file and match its columns to ours: coordinates, time windows, service times and vehicle capacities. We suggest matches from your column headers, and you can change any of them.',
    },
  ];
}

export default buildPricingFaqs;
